import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./UpdateAddress.module.css";

export default function UpdateAddress({ onBack, onUpdate }) {
  // =====================================================
  // URL PARAMETERS
  // =====================================================

  const {
    userId: routeUserIdA,
    deviceUuidA: routeDeviceUuid,
  } = useParams();

  const navigate = useNavigate();


  const API_BASE = "http://localhost/brobizee-api";

  const GET_USER_API = `${API_BASE}/get_user.php`;
  const UPDATE_ADDRESS_API = `${API_BASE}/update_address.php`;

  // =====================================================
  // USER ID / DEVICE UUID
  // =====================================================

  const userIdA =
    routeUserIdA ||
    localStorage.getItem("user_id");

  const deviceUuidA =
    routeDeviceUuid ||
    localStorage.getItem("device_uuid");

  // =====================================================
  // ADDRESS STATE
  // =====================================================

  const [address, setAddress] = useState("");

  const [loading, setLoading] = useState(true);
  const [locationLoading, setLocationLoading] =
    useState(false);

  const [updating, setUpdating] =
    useState(false);

  const [error, setError] =
    useState("");

  const [locationError, setLocationError] =
    useState("");

  // =====================================================
  // AUTO UPDATE TIMER
  // =====================================================

  const [secondsLeft, setSecondsLeft] =
    useState(120);

  // =====================================================
  // ERROR POPUP
  // =====================================================

  const [showNetworkError, setShowNetworkError] =
    useState(false);

  const [networkErrorMessage, setNetworkErrorMessage] =
    useState("");

  // =====================================================
  // ERROR POPUP FUNCTION
  // =====================================================

  const showErrorPopup = useCallback((message) => {
    setNetworkErrorMessage(
      message ||
        "Something went wrong. Please try again."
    );

    setShowNetworkError(true);
  }, []);

  // =====================================================
  // CLOSE ERROR POPUP
  // =====================================================

  const closeErrorPopup = () => {
    setShowNetworkError(false);
    setNetworkErrorMessage("");
  };

  // =====================================================
  // GET USER DATA
  // ONLY ADDRESS
  // =====================================================

  useEffect(() => {
    let cancelled = false;

    const fetchUserAddress = async () => {
      try {
        setLoading(true);
        setError("");

        if (!userIdA) {
          throw new Error(
            "User ID not found."
          );
        }

        const params =
          new URLSearchParams();

        params.append(
          "user_id",
          String(userIdA)
        );

        if (deviceUuidA) {
          params.append(
            "device_uuid",
            String(deviceUuidA)
          );
        }

        const response = await fetch(
          `${GET_USER_API}?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        const responseText =
          await response.text();

        console.log(
          "GET ADDRESS RESPONSE:",
          responseText
        );

        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}`
          );
        }

        let result;

        try {
          result =
            JSON.parse(responseText);
        } catch {
          throw new Error(
            "Invalid JSON returned by get_user.php"
          );
        }

        if (!result.success) {
          throw new Error(
            result.message ||
              "Unable to load address."
          );
        }

        if (cancelled) {
          return;
        }

        const user =
          result.user || {};

        // =================================================
        // ONLY ADDRESS
        // DATABASE COLUMN:
        // location_live
        // =================================================

        const savedAddress =
          user.location_live || "";

        setAddress(savedAddress);

        // =================================================
        // LOCAL STORAGE
        // =================================================

        if (user.id) {
          localStorage.setItem(
            "user_id",
            String(user.id)
          );
        }

        if (user.device_uuid) {
          localStorage.setItem(
            "device_uuid",
            user.device_uuid
          );
        }

        if (savedAddress) {
          localStorage.setItem(
            "user_address",
            savedAddress
          );
        }
      } catch (err) {
        console.error(
          "FETCH ADDRESS ERROR:",
          err
        );

        if (!cancelled) {
          const message =
            err?.message ||
            "Unable to load address.";

          setError(message);

          showErrorPopup(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchUserAddress();

    return () => {
      cancelled = true;
    };
  }, [
    userIdA,
    deviceUuidA,
    showErrorPopup,
  ]);

  // =====================================================
  // GET CURRENT LOCATION
  // =====================================================

  const getCurrentLocation = () => {
    return new Promise(
      (resolve, reject) => {
        if (!navigator.geolocation) {
          reject(
            new Error(
              "Geolocation is not supported by this browser."
            )
          );

          return;
        }

        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0,
          }
        );
      }
    );
  };

  // =====================================================
  // REVERSE GEOCODING
  // =====================================================

  const getAddressFromCoordinates =
    async (
      latitude,
      longitude
    ) => {
      const url =
        "https://nominatim.openstreetmap.org/reverse" +
        `?format=jsonv2` +
        `&lat=${encodeURIComponent(
          latitude
        )}` +
        `&lon=${encodeURIComponent(
          longitude
        )}` +
        `&zoom=18` +
        `&addressdetails=1`;

      const response =
        await fetch(url, {
          method: "GET",
          headers: {
            Accept:
              "application/json",
          },
        });

      if (!response.ok) {
        throw new Error(
          "Unable to get address from current location."
        );
      }

      const data =
        await response.json();

      if (!data.display_name) {
        throw new Error(
          "Address not found from current location."
        );
      }

      return data.display_name;
    };

  // =====================================================
  // UPDATE ADDRESS IN DATABASE
  // =====================================================

  const updateAddressInDatabase = async (currentAddress) => {
  const finalUserIdA =
    userIdA ||
    localStorage.getItem("user_id");

  const finalDeviceUuidA =
    deviceUuidA ||
    localStorage.getItem("device_uuid");

  if (!finalUserIdA) {
    throw new Error("User ID not found.");
  }

  if (!finalDeviceUuidA) {
    throw new Error("Device UUID not found.");
  }

  if (!currentAddress?.trim()) {
    throw new Error("Current address is empty.");
  }

  // =====================================================
  // FORM DATA
  // ONLY LOCATION
  // =====================================================

  const formData = new FormData();

  formData.append(
    "user_id",
    String(finalUserIdA)
  );

  formData.append(
    "device_uuid",
    String(finalDeviceUuidA)
  );

  formData.append(
    "location_live",
    currentAddress.trim()
  );

  // =====================================================
  // ADDRESS ONLY API
  // =====================================================

  const response = await fetch(
    UPDATE_ADDRESS_API,
    {
      method: "POST",
      body: formData,
    }
  );

  const responseText =
    await response.text();

  console.log(
    "ADDRESS ONLY UPDATE RESPONSE:",
    responseText
  );

  if (!response.ok) {
    throw new Error(
      `HTTP ${response.status}: ${responseText}`
    );
  }

  let result;

  try {
    result = JSON.parse(responseText);
  } catch {
    throw new Error(
      "Invalid JSON returned by update_address.php"
    );
  }

  console.log(
    "ADDRESS ONLY UPDATE RESULT:",
    result
  );

  if (!result.success) {
    throw new Error(
      result.message ||
      "Address update failed."
    );
  }

  return result;
};

  // =====================================================
  // UPDATE CURRENT ADDRESS
  // =====================================================

  const updateCurrentAddress =
    useCallback(
      async (showPopupOnSuccess = false) => {
        if (updating) {
          return;
        }

        try {
          setLocationLoading(true);
          setLocationError("");

          const position =
            await getCurrentLocation();

          const latitude =
            position.coords.latitude;

          const longitude =
            position.coords.longitude;

          console.log(
            "CURRENT LATITUDE:",
            latitude
          );

          console.log(
            "CURRENT LONGITUDE:",
            longitude
          );

          // =================================================
          // REVERSE GEOCODE
          // =================================================

          const currentAddress =
            await getAddressFromCoordinates(
              latitude,
              longitude
            );

          console.log(
            "CURRENT ADDRESS:",
            currentAddress
          );

          // =================================================
          // UPDATE DATABASE
          // =================================================

          setUpdating(true);

          await updateAddressInDatabase(
            currentAddress
          );

          // =================================================
          // SET REACT ADDRESS
          // =================================================

          setAddress(
            currentAddress
          );

          // =================================================
          // LOCAL STORAGE
          // =================================================

          localStorage.setItem(
            "current_latitude",
            String(latitude)
          );

          localStorage.setItem(
            "current_longitude",
            String(longitude)
          );

          localStorage.setItem(
            "current_address",
            currentAddress
          );

          localStorage.setItem(
            "user_address",
            currentAddress
          );

          // =================================================
          // CALLBACK
          // =================================================

          if (onUpdate) {
            onUpdate({
              address:
                currentAddress,

              location_live:
                currentAddress,
            });
          }

          console.log(
            "ADDRESS AUTOMATICALLY UPDATED SUCCESSFULLY"
          );

          if (showPopupOnSuccess) {
            // Optional
            // success popup can be added later
          }

          // =================================================
          // RESET TIMER
          // =================================================

          setSecondsLeft(120);
        } catch (err) {
          console.error(
            "AUTO ADDRESS UPDATE ERROR:",
            err
          );

          const message =
            err?.message ||
            "Unable to update current address.";

          setLocationError(message);

          showErrorPopup(message);
        } finally {
          setLocationLoading(false);
          setUpdating(false);
        }
      },
      [
        userIdA,
        deviceUuidA,
        updating,
        onUpdate,
        showErrorPopup,
      ]
    );

  // =====================================================
  // AUTO UPDATE EVERY 2 MINUTES
  // =====================================================

  useEffect(() => {
    if (loading) {
      return;
    }

    /*
     * First automatic location update
     * can be enabled here if required.
     *
     * Current code waits 2 minutes.
     */

    const timer =
      setInterval(() => {
        console.log(
          "2 MINUTES COMPLETED - UPDATING ADDRESS"
        );

        updateCurrentAddress(false);
      }, 120000);

    return () => {
      clearInterval(timer);
    };
  }, [
    loading,
    updateCurrentAddress,
  ]);

  // =====================================================
  // COUNTDOWN TIMER
  // =====================================================

  useEffect(() => {
    if (loading) {
      return;
    }

    const timer =
      setInterval(() => {
        setSecondsLeft(
          (previous) => {
            if (previous <= 1) {
              return 120;
            }

            return previous - 1;
          }
        );
      }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [loading]);

  // =====================================================
  // FORMAT TIMER
  // =====================================================

  const formatTime = (seconds) => {
    const minutes =
      Math.floor(seconds / 60);

    const remainingSeconds =
      seconds % 60;

    return `${String(
      minutes
    ).padStart(2, "0")}:${String(
      remainingSeconds
    ).padStart(2, "0")}`;
  };

  // =====================================================
  // MANUAL UPDATE
  // =====================================================

  const handleManualUpdate =
    async () => {
      if (
        updating ||
        locationLoading
      ) {
        return;
      }

      await updateCurrentAddress(
        true
      );
    };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div
        className={
          styles.screenWrapper
        }
      >
        <div
          className={
            styles.deviceFrame
          }
        >
          <div
            className={
              styles.loadingContainer
            }
          >
            <h1>
              Loading Address...
            </h1>
          </div>
        </div>
      </div>
    );
  }

  // =====================================================
  // MAIN UI
  // =====================================================

  return (
    <>
      <div
        className={
          styles.screenWrapper
        }
      >
        <div
          className={
            styles.deviceFrame
          }
        >
          {/* =================================================
              HEADER
          ================================================= */}

          <header
            className={
              styles.appBar
            }
          >
            <button
              type="button"
              className={
                styles.backBtn
              }
              onClick={() => {
                if (onBack) {
                  onBack();
                } else {
                  navigate(-1);
                }
              }}
              disabled={
                updating ||
                locationLoading
              }
              aria-label="Go Back"
            >
              <svg
                width="20"
                height="16"
                viewBox="0 0 24 16"
                fill="none"
                stroke="#005CA9"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 8H5" />

                <polyline points="12 1 5 8 12 15" />
              </svg>
            </button>
          </header>

          {/* =================================================
              CONTENT
          ================================================= */}

          <div
            className={
              styles.scrollContainer
            }
          >
            <main
              className={
                styles.contentSection
              }
            >
              <h1
                className={
                  styles.pageTitle
                }
              >
                Update Address
              </h1>

              {/* =================================================
                  ADDRESS
              ================================================= */}

              <div
                className={
                  styles.fieldGroup
                }
              >
                <label
                  className={
                    styles.fieldLabel
                  }
                >
                  Address
                </label>

                <div
                  className={
                    styles.inputRow
                  }
                >
                  <input
                    type="text"
                    value={address}
                    readOnly
                    placeholder="Your current address"
                    className={
                      styles.textInput
                    }
                  />

                  <button
                    type="button"
                    className={
                      styles.locationBtn
                    }
                    onClick={
                      handleManualUpdate
                    }
                    disabled={
                      updating ||
                      locationLoading
                    }
                    title="Update Current Location"
                    aria-label="Update Current Location"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#005CA9"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 2v4" />

                      <path d="M12 18v4" />

                      <path d="M2 12h4" />

                      <path d="M18 12h4" />

                      <circle
                        cx="12"
                        cy="12"
                        r="7"
                      />

                      <circle
                        cx="12"
                        cy="12"
                        r="2"
                      />
                    </svg>
                  </button>
                </div>

                {/* =================================================
                    LOCATION STATUS
                ================================================= */}

                {locationLoading && (
                  <small
                    className={
                      styles.locationStatus
                    }
                  >
                    Updating current location...
                  </small>
                )}

                {!locationLoading &&
                  !locationError && (
                    <small
                      className={
                        styles.locationStatus
                      }
                    >
                      Address automatically updates
                      every 2 minutes.
                    </small>
                  )}

                {locationError && (
                  <small
                    className={
                      styles.locationError
                    }
                  >
                    {locationError}
                  </small>
                )}
              </div>

              {/* =================================================
                  TIMER
              ================================================= */}

              <div
                className={
                  styles.timerBox
                }
              >
                <div
                  className={
                    styles.timerLabel
                  }
                >
                  Next automatic update
                </div>

                <div
                  className={
                    styles.timer
                  }
                >
                  {formatTime(
                    secondsLeft
                  )}
                </div>
              </div>

              {/* =================================================
                  UPDATE BUTTON
              ================================================= */}

              <button
                type="button"
                className={
                  styles.updateBtn
                }
                onClick={
                  handleManualUpdate
                }
                disabled={
                  updating ||
                  locationLoading
                }
              >
                {locationLoading
                  ? "Updating Location..."
                  : updating
                  ? "Updating Address..."
                  : "Update Address Now"}
              </button>
            </main>
          </div>
        </div>
      </div>

      {/* =====================================================
          ERROR POPUP
      ===================================================== */}

      {showNetworkError && (
        <div
          className={
            styles.networkScreenWrapper
          }
          onClick={
            closeErrorPopup
          }
        >
          <div
            className={
              styles.networkModalCard
            }
            onClick={(e) =>
              e.stopPropagation()
            }
          >
            <div
              className={
                styles.networkHeader
              }
            >
              <div
                className={
                  styles.networkIcon
                }
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#005CA9"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                  />

                  <line
                    x1="9"
                    y1="9"
                    x2="9.01"
                    y2="9"
                    strokeWidth="3"
                  />

                  <line
                    x1="15"
                    y1="9"
                    x2="15.01"
                    y2="9"
                    strokeWidth="3"
                  />

                  <line
                    x1="9"
                    y1="15"
                    x2="15"
                    y2="15"
                  />
                </svg>
              </div>

              <h2
                className={
                  styles.networkTitle
                }
              >
                Error
              </h2>
            </div>

            <p
              className={
                styles.networkMessage
              }
            >
              {networkErrorMessage}
            </p>

            <button
              type="button"
              className={
                styles.networkOkBtn
              }
              onClick={
                closeErrorPopup
              }
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
}